import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenTrashState } from '@gitbook/app-navigation';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    List,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    type ListRenderItemInfo,
    Page,
    PageMain,
    PagePanels,
    Panel,
    PanelBody,
    toast,
} from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';

import { SpaceIcon } from '../../Components';
import { apiClient, apiQuery } from '../../state';

export function TrashScreen(props: { screen: NavigationScreenTrashState }): React.ReactElement {
    const { screen } = props;

    useHeadTitle('Trash');

    return (
        <Page>
            <PageMain>
                <PagePanels
                    title="Trash"
                    description="Recently deleted spaces can be restored from here. Any space in Trash is permanently deleted after 7 days."
                >
                    <Panel>
                        <Heading>Deleted items</Heading>
                        <PanelBody>
                            <TrashedItemsList organizationId={screen.organization} />
                        </PanelBody>
                    </Panel>
                </PagePanels>
            </PageMain>
        </Page>
    );
}
/**
 * Listing all trashed items.
 */
function TrashedItemsList(props: { organizationId: string }) {
    const { organizationId } = props;

    const { data: content } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId,
        })
    );

    const renderItem = React.useCallback(
        ({ item }: ListRenderItemInfo<api.OrganizationAllSpace>) => {
            return <TrashedSpace space={item.space} />;
        },
        []
    );

    if (content.deletedSpaces.length === 0) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Trashcan />
                </EmptyStateIcon>
                <Heading>No items in the trash</Heading>
                <Description>Your deleted spaces will show up here.</Description>
            </EmptyState>
        );
    }

    return <List items={content.deletedSpaces} renderItem={renderItem} />;
}

function TrashedSpace(props: { space: api.Space }): React.ReactElement | null {
    const { space } = props;

    const restoreTrashedItem = async () => {
        await apiClient.spaces.restoreSpace(space.id);

        toast.success('Welcome back!', {
            description: 'This space has been restored and can now be accessed again.',
        });
    };

    return (
        <ListItem>
            <ListItemIndicator>
                <SpaceIcon space={space} />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{space.title || 'Untitled space'}</ListItemDescription>
                <ListItemDetail>
                    {space.deletedAt ? (
                        <>
                            Deleted <DateShortRelative value={new Date(space.deletedAt)} /> ago
                        </>
                    ) : null}
                </ListItemDetail>
            </ListItemBody>
            <ListItemControls>
                <Button
                    label="Restore"
                    size="medium"
                    kind="secondary"
                    leadingIcon={Icon.Reset}
                    onPress={async () => {
                        await restoreTrashedItem();
                    }}
                />
            </ListItemControls>
        </ListItem>
    );
}
